import { useEffect, useState } from 'react';
import { Nav } from './components/Nav';
import { Seo } from './components/Seo';
import { Toast } from './components/Toast';
import { Footer } from './components/Footer';
import { AboutPage } from './pages/AboutPage';
import { EditorPage } from './pages/EditorPage';
import { HomePage } from './pages/HomePage';
import { SupportPage } from './pages/SupportPage';
import { usePhoto } from './context/PhotoContext';

function currentPath(): string {
  return window.location.pathname === '/' ? '/' : window.location.pathname.replace(/\/$/, '');
}

export function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function App(): JSX.Element {
  const [path, setPath] = useState(currentPath);
  const { error, notice, clearNotice, loadFiles, undo, redo, downloadActive, activePhoto, resetPhoto } = usePhoto();

  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length) void loadFiles(files);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const mod = event.metaKey || event.ctrlKey;
      if (mod && key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }
      if ((mod && key === 'y') || (mod && event.shiftKey && key === 'z')) {
        event.preventDefault();
        redo();
      }
      if (mod && key === 's') {
        event.preventDefault();
        if (activePhoto) void downloadActive();
      }
      if (event.key === 'Escape' && activePhoto) resetPhoto();
    };

    window.addEventListener('paste', onPaste);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activePhoto, downloadActive, loadFiles, redo, resetPhoto, undo]);

  const page =
    path === '/editor' ? (
      <EditorPage />
    ) : path === '/about' ? (
      <AboutPage />
    ) : path === '/support' ? (
      <SupportPage />
    ) : (
      <HomePage />
    );

  return (
    <div className="min-h-screen bg-[color:var(--surface)] text-[color:var(--text)] transition-colors">
      <Seo path={path} />
      <Nav path={path} />
      {page}
      <Footer path={path} />
      <Toast message={error ?? notice} tone={error ? 'error' : 'neutral'} onClose={clearNotice} />
    </div>
  );
}
